import styled from 'styled-components';
import HeadingWithShowAll from 'Components/globalSearch/components/HeadingWithShowAll';
import SectionHeading from 'Components/globalSearch/components/SectionHeading';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { DocHubEntity, Folder } from 'Types/DocumentHubTypes';
import {
  formatNumberWithCommas, imageSource, isFolder, MinWidthApp, valueToObject,
} from 'Utils/UtilityFunctions';
import {
  maxMobile,
  maxTablet,
  maxWidth,
  minExtraLargeScreen, primaryColor, primaryFont, primaryHeadingColor,
  retina, textBlack, wLight, wMedium, wMLight,
} from 'Utils/utilsStyle';
import { Tooltip } from 'antd';
import { actions } from 'Slice/DocumentHubSlice';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFileViewURL } from 'Selector/DocumentHubSelector';
import { actions as flurryActions } from 'Slice/HelperSlice';
import classNames from 'classnames';
import { selectIsPagination } from 'Selector/GlobalSearchResultsSelector';
import BuyerGroupListSkeleton from 'Components/common/skeleton/BuyerGroupListSkeleton';
import { FEED_GILROY_ROUTE, SEARCH_ROUTE } from 'Utils/Constants';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import DrawerDocViewer from './DrawerDocViewer';

const StyleWrapper = styled.div`
&.extendedSearch {
  margin-bottom: 16px;
  @media all and (min-width: ${minExtraLargeScreen}) {
    margin-bottom: 24px;
  }
}
width: 100%;
.document-container {
  display: flex;
  align-items: center;
  padding: 8px 0px;
  column-gap: 10px;
  border: 2px solid transparent;
  &.active {
    border: 2px solid ${primaryColor};
    background: #ECFAF1;
  }
  @media all and (min-width: ${minExtraLargeScreen}) {
    padding: 10px  0px;
    column-gap: 14px;
  }
  @media all and (max-width: ${maxWidth}), ${retina} {
    padding: 6px  0px;
    column-gap: 10px;
  }
  @media all and (max-width:  ${maxTablet}) {
    padding: 4px  0px;
    column-gap: 6px;
  }
  @media all and (max-width: ${maxMobile}) {
    padding: 4px  0px;
    column-gap: 6px;
  }
  &:hover {
    background: #FFF;
    border-radius: 6px;
  }
  .document-logo {
    width: 48px;
    cursor: pointer;
    height: 42px;
    border: 2px solid #D7D3C9;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    @media all and (min-width: ${minExtraLargeScreen}) {
      width: 62px;
      height: 54px;
      border-radius: 6px;
      border: 3px solid #D7D3C9;
    }
    @media all and (max-width: ${maxWidth}), ${retina} {
      width: 48px;
      height: 42px;
      border-radius: 4px;
    }
    @media all and (max-width: ${maxTablet}) {
      width: 38px;
      height: 34px;
      border: 2px solid #D7D3C9;
      border-radius: 3px;
    }
    @media all and (max-width: ${maxMobile}) {
      width: 34px;
      height: 30px;
      border-radius: 4px;
      border: 2px solid #D7D3C9;
    }
    img {
      width: 18px;
      height: 22px;
      @media all and (min-width: ${minExtraLargeScreen}) {
        width: 20px;
        height: 25px;
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        width: 18px;
        height: 22px;
      }
      @media all and (max-width: ${maxTablet}) {
        width: 16px;
        height: 18px;
      }
      @media all and (max-width: ${maxMobile}) {
        width: 12px;
        height: 14px;
      }
    }
  }
  .document-info {
    display: flex;
    flex-direction: column;
    width: calc(100% - 48px);
    @media all and (min-width: ${minExtraLargeScreen}) {
      width: calc(100% - 62px);
    }
    @media all and (max-width: ${maxWidth}), ${retina} {
      width: calc(100% - 48px);
    }
    @media all and (max-width: ${maxTablet}) {
      width: calc(100% - 38px);
    }
    @media all and (max-width: ${maxMobile}) {
      width: calc(100% - 42px);
    }
    .document-name {
      color: ${textBlack};
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMLight};
      line-height: 18px;
      letter-spacing: -0.52px;
      margin-bottom: 6px;
      max-width: calc(100% - 50px);
      cursor: pointer;
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      &.triggers-bullet-tooltip {
        &.person-designation-tip {
          .ant-tooltip {
            .ant-tooltip-content {
              .ant-tooltip-inner {
                white-space: normal;
              }
            }
          }
        }
      }
      @media all and (min-width: ${minExtraLargeScreen}) {
        font-size: 22px;
        line-height: 23px;
        letter-spacing: -0.99px;
        margin-bottom: 8px;
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        font-size: 16px;
        line-height: 18px;
        letter-spacing: -0.52px;
      }
      @media all and (max-width: ${maxTablet}) {
        font-size: 14px;
        line-height: 16px;
        letter-spacing: -0.336px;
        margin-bottom: 4px;
      }
      @media all and (max-width: ${maxMobile}) {
        font-size: 12px;
        line-height: 16px;
        letter-spacing: -0.396px;
        margin-bottom: 2px;
      }
    }
    .details {
      display: flex;
      align-items: center;
      column-gap: 12px;
      cursor: pointer;
      @media all and (min-width: ${minExtraLargeScreen}) {
        column-gap: 16px;
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        column-gap: 12px;
      }
      @media all and (max-width: ${maxTablet}) {
        column-gap: 8px;
      }
      @media all and (max-width: ${maxMobile}) {
        column-gap: 12px;
      }
      .userName {
        color: ${primaryHeadingColor};
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wMedium};
        line-height: 16px;
        letter-spacing: -0.424px;
        margin-bottom: 0px;
        position: relative;
        &::after {
          content: "";
          min-height: 11px;
          min-width: 1px;
          background-color: rgb(207, 207, 207);
          position: absolute;
          right: -7px;
          bottom: 2px;
        }
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 20px;
          letter-spacing: -0.599px;
          line-height: 21px;
          &::after {
            content: "";
            min-height: 15px;
            right: -9px;
            bottom: 0px;
          }
        }
        @media all and (max-width: ${maxWidth}), ${retina} {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.424px;
          &::after {
            min-height: 11px;
            right: -7px;
            bottom: 3px;
          }
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: -0.274px;
          &::after {
            min-height: 8px;
            right: -5px;
            bottom: 3px;
          }
        }
        @media all and (max-width: ${maxMobile}) {
          font-size: 10px;
          line-height: 12px;
          letter-spacing: -0.24px;
          &::after {
            min-height: 10px;
            right: -6px;
            bottom: 2px;
          }
        }
      }
      .date {
        color: ${primaryHeadingColor};
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wLight};
        line-height: 16px;
        letter-spacing: -0.424px;
        margin-bottom: 0px;
        position: relative;
        &::after {
          content: "";
          min-height: 11px;
          min-width: 1px;
          background-color: rgb(207, 207, 207);
          position: absolute;
          right: -7px;
          bottom: 2px;
        }
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 20px;
          letter-spacing: -0.599px;
          line-height: 21px;
          &::after {
            content: "";
            min-height: 15px;
            right: -9px;
            bottom: 0px;
          }
        }
        @media all and (max-width: ${maxWidth}), ${retina} {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.424px;
          &::after {
            min-height: 11px;
            right: -7px;
            bottom: 3px;
          }
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: -0.274px;
          &::after {
            min-height: 8px;
            right: -5px;
            bottom: 3px;
          }
        }
        @media all and (max-width: ${maxMobile}) {
          font-size: 10px;
          line-height: 12px;
          letter-spacing: -0.24px;
          &::after {
            min-height: 10px;
            right: -6px;
            bottom: 2px;
          }
        }
      }
      .fileSize {
        color: ${primaryHeadingColor};
        font-family: ${primaryFont};
        font-size: 14px;
        font-weight: ${wLight};
        line-height: 16px;
        letter-spacing: -0.424px;
        margin-bottom: 0px;
        @media all and (min-width: ${minExtraLargeScreen}) {
          font-size: 20px;
          letter-spacing: -0.599px;
          line-height: 21px;
        }
        @media all and (max-width: ${maxWidth}), ${retina} {
          font-size: 14px;
          line-height: 16px;
          letter-spacing: -0.424px;
        }
        @media all and (max-width: ${maxTablet}) {
          font-size: 12px;
          line-height: 14px;
          letter-spacing: -0.274px;
        }
        @media all and (max-width: ${maxMobile}) {
          font-size: 10px;
          line-height: 12px;
          letter-spacing: -0.24px;
        }
      }

    }
  }
  &:last-child {
    margin-bottom: 0px;
  }
}
`;

type Props = {
  heading?: string,
  count?: number,
  loading?: boolean,
  entitiesCount?: DocHubEntity[],
  entitiesToDisplay?: DocHubEntity[]
  inGlobalSearch?: boolean,
  searchQuery?: string,
} & typeof defaultProps;

const defaultProps = {
  heading: '',
  count: 0,
  loading: false,
  entitiesCount: [] as DocHubEntity[],
  entitiesToDisplay: [] as DocHubEntity[],
  inGlobalSearch: false,
  searchQuery: '',
};
const DocumentHubListView = function DocumentHubListView(props: Props) {
  const [searchParams] = useSearchParams();
  const [drawerOpenForDoc, setDrawerOpenForDoc] = useState(false);
  const [tableRowId, setTableRowId] = useState<number | null>();
  const extendedSections = ['person', 'company', 'article', 'industry', 'group', 'document'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const isDocumentSearchPaginated: boolean = useSelector(selectIsPagination);
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const {
    entitiesToDisplay, heading, count, loading, entitiesCount, inGlobalSearch,
    searchQuery,
  } = props;
  const formattedCount = formatNumberWithCommas(count);

  const dispatch = useDispatch();
  const previewDocument = useSelector(selectFileViewURL);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const host = window.location.href;
  const isMinWidth = MinWidthApp();
  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const name = (entity: DocHubEntity) => (isFolder(entity)
    ? (entity).name : (entity).fileName);
  const username = (entity: DocHubEntity) => (isFolder(entity)
    ? (entity).createdBy : (entity).username);

  const createDocumentsRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_documents_see_all_clicked', 0, {
        host,
      }),
    ));
    window.scrollTo(0, 0);
    return navigate(`${pathname}?query=${searchQuery}&extend=document`, { state: { enableBackButton: true, previousPath } });
  };

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${searchQuery}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  useEffect(() => {
    const node = document.querySelector('body');
    return drawerOpenForDoc ? node?.classList.add('body-drawer-alerts-toggle') : node?.classList.remove('body-drawer-alerts-toggle');
  }, [drawerOpenForDoc]);

  const handleClick = (entity: DocHubEntity) => {
    if (isFolder(entity)) {
      dispatch(actions.setCurrentFolder(entity as Folder));
      if (inGlobalSearch) {
        dispatch(actions.setFromGlobalSearch(true));
        navigate('/documents');
      }
    } else if (!isMinWidth) {
      const { id } = entity;
      dispatch(actions.getPreviewDocument({ id, extension: (entity).extension, isSap: true }));
      setDrawerOpenForDoc(true);
      setTableRowId(id);
    }
  };

  return (
    entitiesToDisplay.length > 0 || loading ? (
      <StyleWrapper
        className={classNames({
          extendedSearch: isDetailedSearch,
        })}
      >
        {
          loading && !isDetailedSearch ? (
            <BuyerGroupListSkeleton />
          ) : (
            <div>
              <HeadingWithShowAll
                inGlobalSearch={inGlobalSearch}
                isShowAllHidden={isDetailedSearch ? true
                  : entitiesCount && entitiesCount.length <= 2}
                leftHeading={(
                  <SectionHeading customClassName="global-search-heading">{`${heading} (${formattedCount})`}</SectionHeading>)}
                onClick={createDocumentsRoute}
                onSeelessClick={navigateToSearchResult}
                isDetailedSearch={isDetailedSearch}
              />
              {
                entitiesToDisplay?.map((entity) => (
                  <div className={classNames('document-container', { active: tableRowId === entity?.id && !isMinWidth })}>
                    <div className="document-logo" onClick={() => handleClick(entity)}>
                      <img
                        src={imageSource(isFolder(entity) ? (entity).isSharedFolder
                          ? 'sharedFolder' : (entity).collaborators?.length ? 'companyFolder'
                            : 'folder' : (entity).extension)}
                        alt=""
                      />
                    </div>
                    <div className="document-info">
                      <Tooltip
                        trigger="hover"
                        placement="topLeft"
                        getPopupContainer={(e: HTMLElement) => e}
                        className="triggers-bullet-tooltip text-truncate-one-line person-designation-tip"
                        color="#fff"
                        arrow={false}
                        title={name(entity)}
                      >
                        <span
                          className="document-name"
                          onClick={() => handleClick(entity)}
                          onKeyDown={() => handleClick(entity)}
                          role="button"
                          tabIndex={0}
                        >
                          {name(entity)}
                        </span>
                      </Tooltip>
                      <div className="details">
                        <Tooltip
                          trigger="hover"
                          placement="top"
                          getPopupContainer={(e: HTMLElement) => e}
                          className="triggers-bullet-tooltip person-designation-tip"
                          color="#fff"
                          arrow={false}
                          title={username(entity)}
                        >
                          <p className="userName">{username(entity)}</p>
                        </Tooltip>
                        <Tooltip
                          trigger="hover"
                          placement="top"
                          getPopupContainer={(e: HTMLElement) => e}
                          className="triggers-bullet-tooltip person-designation-tip"
                          color="#fff"
                          arrow={false}
                          title={entity.date}
                        >
                          <p className="date">{entity.date}</p>
                        </Tooltip>
                        <Tooltip
                          trigger="hover"
                          placement="top"
                          getPopupContainer={(e: HTMLElement) => e}
                          className="triggers-bullet-tooltip person-designation-tip"
                          color="#fff"
                          arrow={false}
                          title={isFolder(entity)
                            ? `${(entity).totalItems} items` : (entity).fileSize}
                        >
                          <p className="fileSize">
                            {isFolder(entity)
                              ? `${(entity).totalItems} items` : (entity).fileSize}
                          </p>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                ))
              }
              {drawerOpenForDoc && (
                <DrawerDocViewer
                  drawerOpen={drawerOpenForDoc}
                  setDrawerOpen={setDrawerOpenForDoc}
                  URL={previewDocument}
                  setTableRowId={setTableRowId}
                />
              )}
              {inGlobalSearch && isDocumentSearchPaginated && <BuyerGroupListSkeleton />}
            </div>
          )
        }
      </StyleWrapper>
    ) : (
      null
    )
  );
};
DocumentHubListView.defaultProps = defaultProps;
export default DocumentHubListView;
